import type { CampaignDayCardStep } from '@poseidon/components/CampaignDayCard';
import type { CadenceStepStatePure, CadenceStepTypePure } from '@poseidon/components/CGStep';
import type { SequenceArmPathSeg } from '@poseidon/types/types/sequenceOverrideKey';
import {
  DEFAULT_AWAIT_DEADLINE_DAYS,
  NOT_ACCEPTED_SEG,
  sequenceOverrideKey,
} from '@poseidon/types/types/sequenceOverrideKey';

import type {
  SequenceAnyStepType,
  SequenceContentOverrides,
  SequenceStep,
  SequenceSwitchAttribute,
} from '~/components/Sequence/sequenceSteps';
import type { SequenceContactStepStatus } from '~/components/Sequence/sequenceStepStatus';
import {
  DEFAULT_LIKE_COUNT,
  DEFAULT_WAIT_DAYS,
  isEmailThreadContinued,
  previousEmailSubject,
  sequenceStepIssue,
  stepTakesContactOverride,
  switchBranchLabel,
} from '~/components/Sequence/sequenceSteps';
import { isStepContentEditableNow, sequenceStepVisual } from '~/components/Sequence/sequenceStepStatus';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * One contact's sequence, cut into the campaign timeline's pieces — so the contact page draws a sequence
 * with the SAME day cards, wait pills and "did they accept?" card it draws a campaign with. Waits split the
 * steps into days; everything between two waits is one day card. A parallel group's steps share a day. The
 * not-accepted lane nests inside the accept card, exactly like a campaign's No branch. Spawn and switch have
 * no campaign equivalent and get their own cards in the same visual language.
 */
export type SequenceTimelineBlock =
  | { kind: 'day'; key: string; date: Date; steps: CampaignDayCardStep[] }
  | { kind: 'wait'; key: string; days: number }
  | {
      kind: 'await';
      key: string;
      deadlineDays: number;
      /** The campaign card's state: `skipped` = accepted (hides the No branch). */
      state: CadenceStepStatePure;
      /** The lane opens with a "Withdraw invite" — shown as the card's Auto-withdraw: On. */
      autoWithdraw: boolean;
      /** The not-accepted lane, minus its withdraw. */
      no: SequenceTimelineBlock[];
    }
  | { kind: 'spawn'; key: string; blocks: SequenceTimelineBlock[] }
  | { kind: 'switch'; key: string; attribute: SequenceSwitchAttribute; routes: SequenceTimelineRoute[] };

export interface SequenceTimelineRoute {
  label: string;
  /** The contact matched a different route — they will never walk this one. */
  notTaken: boolean;
  /** The route has no steps at all — a contact who matches it is done. */
  empty: boolean;
  blocks: SequenceTimelineBlock[];
}

/** What the cutter reads for one contact. */
export interface SequenceTimelineInput {
  /** This contact's status for a step key, already adjusted for prep. */
  statusAt: (key: string) => SequenceContactStepStatus | undefined;
  /** This contact's own text, by step key — a step with an entry reads as edited from the template. */
  overrides: SequenceContentOverrides;
}

/** Sequence step type → the campaign row that draws it. Absent = not a row (wait / await / containers / withdraw). */
const ROW_TYPE: Partial<Record<SequenceAnyStepType, CadenceStepTypePure>> = {
  message: 'message',
  connect: 'connect',
  inmail: 'inmail',
  email: 'email',
  like: 'likeRecentPosts',
  follow: 'follow',
  call: 'call',
  reminder: 'reminder',
};

/** Campaign states the timeline hides: it has already run, or never will (the campaign timeline's own rule). */
const SETTLED_STATES: ReadonlySet<CadenceStepStatePure> = new Set(['finished', 'skipped', 'canceled']);

/** A step's campaign state for this contact. A to-do waiting on the rep is `action-required` (green + Mark done). */
export function campaignStateFor(status: SequenceContactStepStatus | undefined): CadenceStepStatePure {
  if (status == null) return 'not-started';
  if (status.state === 'done') return 'finished';
  if (status.state === 'skipped') return 'skipped';
  if (status.state === 'not_taken') return 'canceled';
  if (status.state === 'errored') return 'interrupted';
  if (status.state === 'current' && sequenceStepVisual(status)?.tone === 'needsYou') return 'action-required';
  return 'not-started';
}

/**
 * One sequence step as a campaign row: this contact's text, its state, its problem (red), and whether the rep
 * can rewrite it here. Null for a step that isn't a row.
 */
export function toCampaignRow(
  step: SequenceStep,
  key: string,
  line: SequenceStep[],
  index: number,
  ordered: boolean,
  input: SequenceTimelineInput,
): CampaignDayCardStep | null {
  const type = ROW_TYPE[step.type];
  if (type == null) return null;
  const status = input.statusAt(key);
  const issue = sequenceStepIssue(line, index);
  // A threaded email inherits the previous subject: hand the editor that subject (it then hides its own).
  const threaded = step.type === 'email' && ordered && isEmailThreadContinued(line, index);
  return {
    id: key,
    type,
    state: campaignStateFor(status),
    body2: step.body ?? null,
    subject: step.subject ?? null,
    postsToLike: step.type === 'like' ? (step.likeCount ?? DEFAULT_LIKE_COUNT) : null,
    replyToPrevious: step.type === 'email' ? threaded : null,
    previousEmailSubject: threaded ? previousEmailSubject(line, index) : null,
    isDifferentFromTemplate: input.overrides[key] != null,
    error: issue?.level === 'error' ? issue.message : null,
    editable: stepTakesContactOverride(step) && isStepContentEditableNow(status),
  };
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * DAY_MS);
}

/** Every step of a lane is dead for this contact — they will never walk it. */
function laneNotTaken(steps: SequenceStep[], path: SequenceArmPathSeg[], input: SequenceTimelineInput): boolean {
  return steps.length > 0 && steps.every((_, j) => input.statusAt(sequenceOverrideKey(path, j))?.state === 'not_taken');
}

function isSettled(status: SequenceContactStepStatus | undefined): boolean {
  return status?.state === 'done' || status?.state === 'skipped' || status?.state === 'not_taken';
}

/** The contact has got to this step (it ran, was skipped past, or is where they are now). */
function isReached(status: SequenceContactStepStatus | undefined): boolean {
  const state = status?.state;
  return state === 'done' || state === 'skipped' || state === 'current' || state === 'errored';
}

/**
 * Cut one line (spine, lane, route) into timeline blocks, recursing into what hangs off it. Like the campaign
 * timeline, what has already run — or never will — is left out; a day with nothing left disappears with its
 * wait. Dates are the earliest each day can land, counting this line's remaining waits forward from `start`.
 */
export function sequenceTimelineBlocks(
  steps: SequenceStep[],
  path: SequenceArmPathSeg[],
  start: Date,
  input: SequenceTimelineInput,
): SequenceTimelineBlock[] {
  const blocks: SequenceTimelineBlock[] = [];
  let date = start;
  let day: CampaignDayCardStep[] = [];
  let dayKey: string | null = null;
  const flushDay = () => {
    if (dayKey != null && day.length > 0) blocks.push({ kind: 'day', key: dayKey, date, steps: day });
    day = [];
    dayKey = null;
  };
  const addRow = (row: CampaignDayCardStep | null, key: string) => {
    if (row == null || (row.state != null && SETTLED_STATES.has(row.state))) return;
    dayKey ??= key;
    day.push(row);
  };

  steps.forEach((step, i) => {
    const key = sequenceOverrideKey(path, i);
    const status = input.statusAt(key);

    if (step.type === 'wait') {
      flushDay();
      // The engine moves the cursor past a wait as it starts it, so a wait the contact is counting down right
      // now already reads done — the step after it carries when it ends. Still a wait to them, so it stays.
      const waitEndsAt = input.statusAt(sequenceOverrideKey(path, i + 1))?.waitEndsAt;
      if (isSettled(status) && waitEndsAt == null) return;
      const days = step.waitDays ?? DEFAULT_WAIT_DAYS;
      blocks.push({ kind: 'wait', key, days });
      date = waitEndsAt ?? addDays(date, days);
      return;
    }

    if (step.type === 'await_connect') {
      flushDay();
      const lane = step.branches?.notAccepted ?? [];
      const lanePath = [...path, i, NOT_ACCEPTED_SEG];
      const accepted = laneNotTaken(lane, lanePath, input);
      // Past it with nothing left inside — accepted (its No lane will never run) or a gate with no No lane at
      // all: the question is answered. History, like a sent step.
      if (isSettled(status) && (accepted || lane.length === 0)) return;
      const deadlineDays = step.deadlineDays ?? DEFAULT_AWAIT_DEADLINE_DAYS;
      blocks.push({
        kind: 'await',
        key,
        deadlineDays,
        state: accepted ? 'skipped' : 'not-started',
        autoWithdraw: lane[0]?.type === 'withdraw',
        no: accepted ? [] : sequenceTimelineBlocks(lane, lanePath, addDays(date, deadlineDays), input),
      });
      return;
    }

    if (step.type === 'spawn') {
      flushDay();
      const arm = sequenceTimelineBlocks(step.arm ?? [], [...path, i, 'spawn'], date, input);
      if (arm.length > 0) blocks.push({ kind: 'spawn', key, blocks: arm });
      return;
    }

    if (step.type === 'switch') {
      flushDay();
      const routing = step.switch;
      if (routing == null) return;
      // Routed once a route is dead (another won) or has started. An EMPTY route has no status of its own:
      // it lost too if, once routed, a route with steps is still alive.
      const routePath = (bi: number) => [...path, i, bi];
      const routed = routing.branches.some(
        (b, bi) =>
          laneNotTaken(b.arm, routePath(bi), input) || isReached(input.statusAt(sequenceOverrideKey(routePath(bi), 0))),
      );
      const liveRouteWithSteps = routing.branches.some(
        (b, bi) => b.arm.length > 0 && !laneNotTaken(b.arm, routePath(bi), input),
      );
      blocks.push({
        kind: 'switch',
        key,
        attribute: routing.attribute,
        routes: routing.branches.map((b, bi) => {
          const notTaken = b.arm.length > 0 ? laneNotTaken(b.arm, routePath(bi), input) : routed && liveRouteWithSteps;
          return {
            label: switchBranchLabel(routing.attribute, b),
            notTaken,
            empty: b.arm.length === 0,
            blocks: notTaken ? [] : sequenceTimelineBlocks(b.arm, routePath(bi), date, input),
          };
        }),
      });
      return;
    }

    if (step.type === 'group') {
      // Fired together → one day. The chip row is the line a grouped step is judged in (as on the canvas).
      const chips = (step.arms ?? []).map((arm) => arm[0]).filter((s): s is SequenceStep => s != null);
      chips.forEach((chip, a) =>
        addRow(toCampaignRow(chip, sequenceOverrideKey([...path, i, a], 0), chips, a, false, input), key),
      );
      return;
    }

    addRow(toCampaignRow(step, key, steps, i, true, input), key);
  });
  flushDay();
  return blocks;
}
